import { useState } from 'react';

import { AudioSpeakerTaggingPanel } from './audio-speaker-tagging-panel';
import type { AudioSpeakerFiles } from '@/api/types/backend-dto-types';
import { Drawer } from '@/new/components/drawers/drawer';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/new/components/shadcn/select';

/** Only ever shown for files that still need work — done files get a tick instead. */
function untaggedLabel(file: AudioSpeakerFiles[number]): string {
  return `${file.untaggedSpeakerCount} unidentified ${file.untaggedSpeakerCount === 1 ? 'speaker' : 'speakers'}`;
}

/** Progress across the whole set, so the header says why the drawer is open. */
function progressLabel(files: AudioSpeakerFiles): string {
  const untaggedCount = files.filter((file) => file.untaggedSpeakerCount > 0).length;

  if (untaggedCount === 0) {
    return `All speakers assigned across ${files.length} audio ${files.length === 1 ? 'file' : 'files'}`;
  }
  if (files.length === 1) return 'This file still has unidentified speakers';
  return `${untaggedCount} of ${files.length} files still have unidentified speakers`;
}

interface AudioReviewDrawerContentProps {
  matterId: string;
  files: AudioSpeakerFiles;
  onClose: () => void;
}

function AudioReviewDrawerContent({ matterId, files, onClose }: AudioReviewDrawerContentProps) {
  const [selectedDocId, setSelectedDocId] = useState(
    () => (files.find((file) => file.untaggedSpeakerCount > 0) ?? files[0])?.docId,
  );
  const selectedFile = files.find((file) => file.docId === selectedDocId) ?? files[0];
  const selectedIndex = files.findIndex((file) => file.docId === selectedFile?.docId);

  return (
    <div className="flex h-full flex-col text-sm text-gray-600">
      <div className="shrink-0 border-b border-gray-200 px-6 py-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 items-center gap-2.5">
            <h2 className="text-sm font-medium text-gray-900">Review audio speakers</h2>
            <span className="size-0.75 rounded-full bg-gray-400"></span>
            <p className="text-sm text-gray-500">{progressLabel(files)}</p>
          </div>

          <Button variant="ghost" size="iconSm" className="-mt-1 -mr-2 shrink-0" onClick={onClose} aria-label="Close">
            <Icon className="lucide--x" />
          </Button>
        </div>

        <div className="-mx-1 mt-2.5 flex items-center gap-2">
          <Select value={selectedFile?.docId} onValueChange={setSelectedDocId}>
            <SelectTrigger aria-label="Audio file to review">
              <SelectValue>{selectedFile?.fileName}</SelectValue>
            </SelectTrigger>
            <SelectContent>
              {files.map((file) => (
                <SelectItem key={file.docId} value={file.docId} className="[&>span+span]:min-w-0 [&>span+span]:flex-1">
                  <span className="flex w-full min-w-0 items-center gap-2">
                    {file.untaggedSpeakerCount === 0 ? (
                      <Icon className="lucide--check-circle-2 text-green-500" aria-hidden />
                    ) : (
                      <Icon className="lucide--alert-circle text-amber-500" aria-hidden />
                    )}
                    <span className="truncate">{file.fileName}</span>
                    {file.untaggedSpeakerCount > 0 && (
                      <span className="ml-auto shrink-0 text-xs text-gray-500">{untaggedLabel(file)}</span>
                    )}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {files.length > 1 && (
            <div className="flex shrink-0 items-center gap-0.5">
              <Button
                variant="ghost"
                disabled={selectedIndex <= 0}
                onClick={() => setSelectedDocId(files[selectedIndex - 1].docId)}
                aria-label="Previous audio file"
              >
                <Icon className="lucide--chevron-left" />
              </Button>
              <span className="px-1 text-xs text-gray-500 tabular-nums">
                Audio file {selectedIndex + 1} of {files.length}
              </span>
              <Button
                variant="ghost"
                disabled={selectedIndex >= files.length - 1}
                onClick={() => setSelectedDocId(files[selectedIndex + 1].docId)}
                aria-label="Next audio file"
              >
                <Icon className="lucide--chevron-right" />
              </Button>
            </div>
          )}
        </div>
      </div>

      {selectedFile && (
        // Keyed so switching files remounts the panel and resets playback
        <div key={selectedFile.docId} className="flex min-h-0 flex-1 flex-col">
          <AudioSpeakerTaggingPanel matterId={matterId} docId={selectedFile.docId} />
        </div>
      )}
    </div>
  );
}

interface AudioReviewDrawerProps {
  matterId: string;
  files: AudioSpeakerFiles;
  isOpen: boolean;
  onClose: () => void;
}

export function AudioReviewDrawer({ matterId, files, isOpen, onClose }: AudioReviewDrawerProps) {
  return (
    <Drawer isOpen={isOpen} onClose={onClose} hideCloseButton>
      <AudioReviewDrawerContent matterId={matterId} files={files} onClose={onClose} />
    </Drawer>
  );
}
